import { useRef } from "react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Skeleton } from "primereact/skeleton";
import { connect } from "react-redux";

import * as actions from "../../store/actions/index";
import { withRouter } from "../../shared/utility";
import EmptyModal from "../../components/modals/EmptyModal";
import ReceivingItemLookupResult from "../../components/forms/traceability/receivingBarcode/ReceivingItemLookupResult";
import PromoteReceivingToPackagingModal from "../../components/inventory/PromoteReceivingToPackagingModal";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import { TraceabilityReceivingItemLookupViewModel } from "../../viewmodels/traceability/TraceabilityReceivingItemLookupViewModel";

function TraceabilityReceivingItemLookup(props) {
  const { logout, addSnackbar } = props;
  const {
    loading,
    item,
    invoice,
    histories,
    hasSearched,
    notFound,
    onLookupBarcode,
    emptyModal,
    setEmptyModelOpen,
    promoteModal,
    openPromoteModal,
    closePromoteModal,
    handlePromoteSuccess,
    getPromoteReceivingDefaults,
    promoteReceivingToPackaging,
  } = TraceabilityReceivingItemLookupViewModel(logout);

  const searchRef = useRef();

  const handleSearch = () => {
    onLookupBarcode(searchRef.current?.value);
  };

  const onPromoteSuccess = async () => {
    await handlePromoteSuccess();
    addSnackbar(
      new SnackbarData(
        generateUniqueNumber(),
        "Item promoted to sellable stock",
        SnackbarType.Success,
      ),
    );
  };

  const hasResult = item != null;

  return (
    <div className="mt-2 lg:px-8">
      <div className="sm:flex sm:items-center px-4 py-5 card-custom">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold leading-6 text-gray-900">
            Receiving Item Lookup
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Scan or paste a receiving barcode to view item details, invoice
            context, and full receiving timeline.
          </p>
          <div className="mt-5 flex rounded-md">
            <div className="relative flex items-stretch flex-grow focus-within:z-10">
              <input
                type="search"
                name="receivingBarcode"
                id="receivingBarcode"
                className="block w-full rounded-lg rounded-l-md pl-3 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                placeholder="Enter receiving barcode or serial (last 10 digits)"
                ref={searchRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
            </div>
            <button
              type="button"
              className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
              onClick={handleSearch}
            >
              <MagnifyingGlassIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="py-4 space-y-4">
          <Skeleton className="w-full" height="8rem" borderRadius="16px"></Skeleton>
          <Skeleton className="w-full" height="7rem" borderRadius="16px"></Skeleton>
          <Skeleton className="w-full" height="7rem" borderRadius="16px"></Skeleton>
          <Skeleton className="w-full" height="7rem" borderRadius="16px"></Skeleton>
        </div>
      ) : null}

      {!loading && hasResult ? (
        <ReceivingItemLookupResult
          item={item}
          invoice={invoice}
          histories={histories}
          onPromote={openPromoteModal}
        />
      ) : null}

      {!loading && hasSearched && notFound ? (
        <div className="mt-4 card-custom rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <h2 className="text-lg font-semibold text-gray-900">Item not found</h2>
          <p className="mt-2 text-sm text-gray-500">
            We could not resolve this receiving barcode.
          </p>
        </div>
      ) : null}

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <EmptyModal
        wide
        childComponent={
          promoteModal.receivingItemId ? (
            <PromoteReceivingToPackagingModal
              receivingItemId={promoteModal.receivingItemId}
              onSuccess={onPromoteSuccess}
              onCancel={closePromoteModal}
              getPromoteReceivingDefaults={getPromoteReceivingDefaults}
              promoteReceivingToPackaging={promoteReceivingToPackaging}
              logout={logout}
            />
          ) : null
        }
        open={promoteModal.open}
        setOpen={(next) => {
          if (!next.open) {
            closePromoteModal();
          }
        }}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TraceabilityReceivingItemLookup),
);
